<template>
  <FriedModal :size="ModalSize.Large" @close="$emit('close')">
    <template #header>
      <FriedModalHeader :header="title" />
    </template>
    <div class="recipient-modal">
      <Sender class="sender" :sender="sender" :company="company" />
      <div v-if="isSigningOrder" class="signing-order-message">
        <FriedH5>{{ signingOrderMessage }}</FriedH5>
      </div>
      <RecipientTimeline
        class="recipients"
        :recipients="recipients"
        :recipient="recipient"
        :is-signing-order="isSigningOrder"
        :recipients-ssn="recipientsSsn"
        :ssn-fetched="ssnFetched"
      />
    </div>
    <template #footer>
      <FriedModalButtonsFooter v-if="showInviteButton">
        <FriedTooltip :disabled="canInvite" :content="$t('cr_max_recipients_reached_tooltip')">
          <FriedButton
            id="invite-button"
            data-external="recipents-modal-invite-button"
            class="invite-button"
            :disabled="!canInvite"
            @click="$emit('invite')"
          >
            {{ $t('invite-recipient') }}
          </FriedButton>
        </FriedTooltip>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>
<script lang="ts">
import type { SigningPageUser } from '@getaccept/lib-shared-new/src/types/signing-page-user';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { SigningPageDocument } from '@getaccept/lib-shared-new/src/types/signing-page-document';
import { DocumentStatus } from '@getaccept/lib-shared-new/src/enums/document-status';
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted } from 'vue';
import { ModalSize } from '@getaccept/fried-tofu';
import { useAsyncState } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import { DocumentService } from '../../api/documents/services/document.service';
import Sender from './Sender.vue';
import RecipientTimeline from './RecipientTimeline.vue';

export default defineComponent({
  components: {
    Sender,
    RecipientTimeline,
  },
  props: {
    document: {
      type: Object as PropType<SigningPageDocument>,
      required: true,
    },
    company: {
      type: String,
      default: '',
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      required: true,
    },
    isSigningOrder: {
      type: Boolean,
      default: false,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    currentRecipientsTurnToSign: {
      type: Boolean,
      default: false,
    },
    recipientsTurnToSign: {
      type: Object as PropType<SigningPageRecipient>,
      default: undefined,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    canInvite: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['invite', 'close'],
  setup(props) {
    const { t } = useI18n();

    const title = computed((): string => `${t('recipients')} (${props.recipients.length})`);

    const showInviteButton = computed((): boolean => {
      if (props.isPreview) {
        return false;
      }
      return ![
        DocumentStatus.Signed,
        DocumentStatus.Recalled,
        DocumentStatus.Rejected,
        DocumentStatus.Lost,
      ].includes(props.document.status);
    });

    const signingOrderMessage = computed((): string => {
      if (!props.isSigningOrder) {
        return '';
      }

      if (props.currentRecipientsTurnToSign) {
        return t('your-turn-to-sign');
      }

      if (!props.recipientsTurnToSign) {
        return t('other-turn-to-sign-hidden');
      }

      return t('other-turn-to-sign', { name: props.recipientsTurnToSign.fullName });
    });

    const { state, isReady, execute: loadRecipientsSSN } = useAsyncState(
      DocumentService.getRecipientsSSN(),
      null
    );

    const recipientsSsn = computed(() => state.value);
    const ssnFetched = computed(() => isReady.value);

    onMounted(() => {
      loadRecipientsSSN();
    });

    return {
      ModalSize,
      title,
      showInviteButton,
      signingOrderMessage,
      recipientsSsn,
      ssnFetched,
    };
  },
});
</script>
<style lang="scss" scoped>
.recipient-modal {
  .signing-order-message {
    color: var(--text-gray);

    h5 {
      font-weight: normal;
    }
  }

  .recipients {
    margin-top: var(--spacing-200);
  }

  .sender {
    margin-bottom: var(--spacing-200);
  }

  @media only screen and (max-width: $xs) {
    overflow-y: scroll;

    .recipients {
      margin-top: var(--spacing-100);
    }

    .sender {
      margin-bottom: var(--spacing-100);
    }
  }
}

:deep(.ga-modal-container) {
  @media only screen and (max-width: $xs) {
    overflow-y: scroll;
  }

  > .ga-modal-footer {
    padding: var(--spacing-150);
    padding-top: 0;

    .ga-button {
      margin-left: 0;
      background-color: var(--brand-orange);
      color: var(--white);
    }

    @media only screen and (max-width: $xs) {
      .ga-button {
        width: 100%;
        flex-direction: row;
        justify-content: center;
      }
    }
  }
}

.invite-button {
  width: 100%;
}
</style>
